import React, { useEffect, useMemo, useState } from "react";
import {
    Button,
    Group,
    Paper,
    Stack,
    Table,
    Text,
    TextInput,
    ActionIcon,
    Modal,
    FileInput,
    Loader,
    Badge,
    Select,
    Container,
    Grid,
    Switch,
    MultiSelect,
    Divider,
    Alert,
} from "@mantine/core";
import {
    IconArrowUp,
    IconArrowDown,
    IconTrash,
    IconPencil,
    IconUpload,
    IconFolderPlus,
    IconAlertCircle,
} from "@tabler/icons-react";
import HeroSectionV2 from "../../../components/HeroSectionV2";
import Billede1 from "../../../Images/Billede1.jpg";
import { getAuthHeaders } from "../../../api/authHelper";

type EventOption = { value: string; label: string };
type FunctionOption = { value: string; label: string };

type EventFileDto = {
    fileID: number;
    eventID: number;
    eventFileSectionID?: number | null;
    name: string;
    originalFileName: string;
    contentType?: string | null;
    sizeBytes: number;
    sortOrder: number;
    createdAt: string;
    downloadUrl: string;
};

type EventFileSectionDto = {
    eventFileSectionID: number;
    eventID: number;
    name: string;
    description?: string | null;
    sortOrder: number;
    isActive: boolean;
    isPublic: boolean;
    functionIds: number[];
    functionNames: string[];
    files: EventFileDto[];
};

function formatBytes(bytes: number) {
    if (!bytes) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const v = bytes / Math.pow(k, i);
    return `${v.toFixed(i === 0 ? 0 : 1)} ${sizes[i]}`;
}

export default function EventFilesAdminPage() {
    const apiUrl = process.env.REACT_APP_API_URL as string;

    const [eventId, setEventId] = useState<number | null>(null);
    const [eventOptions, setEventOptions] = useState<EventOption[]>([]);
    const [eventsLoading, setEventsLoading] = useState(true);

    const [functionOptions, setFunctionOptions] = useState<FunctionOption[]>([]);
    const [functionsLoading, setFunctionsLoading] = useState(false);

    const [loading, setLoading] = useState(false);
    const [sections, setSections] = useState<EventFileSectionDto[]>([]);
    const [pageError, setPageError] = useState<string | null>(null);

    // upload
    const [uploadFile, setUploadFile] = useState<File | null>(null);
    const [uploadName, setUploadName] = useState("");
    const [uploadSectionId, setUploadSectionId] = useState<string | null>(null);
    const [uploading, setUploading] = useState(false);

    // create/edit section
    const [sectionModalOpen, setSectionModalOpen] = useState(false);
    const [sectionEditItem, setSectionEditItem] = useState<EventFileSectionDto | null>(null);
    const [sectionName, setSectionName] = useState("");
    const [sectionDescription, setSectionDescription] = useState("");
    const [sectionIsActive, setSectionIsActive] = useState(true);
    const [sectionIsPublic, setSectionIsPublic] = useState(false);
    const [sectionFunctionIds, setSectionFunctionIds] = useState<string[]>([]);
    const [sectionSaving, setSectionSaving] = useState(false);

    // edit file
    const [fileModalOpen, setFileModalOpen] = useState(false);
    const [fileEditItem, setFileEditItem] = useState<EventFileDto | null>(null);
    const [fileEditName, setFileEditName] = useState("");
    const [fileEditSectionId, setFileEditSectionId] = useState<string | null>(null);
    const [fileSaving, setFileSaving] = useState(false);

    async function loadEvents() {
        setEventsLoading(true);
        try {
            const res = await fetch(`${apiUrl}/api/events/light`);
            if (!res.ok) {
                setEventOptions([]);
                return;
            }

            const data = await res.json();

            const options: EventOption[] = (data as any[]).map((x) => {
                const id = x.eventID ?? x.EventID ?? x.id ?? x.Id;
                const name = x.eventName ?? x.EventName ?? x.name ?? x.Name ?? `Event ${id}`;
                return {
                    value: String(id),
                    label: String(name),
                };
            });

            setEventOptions(options);
        } finally {
            setEventsLoading(false);
        }
    }

    async function loadFunctions(eid: number) {
        setFunctionsLoading(true);
        try {
            // Ret endpoint hvis dit functions-endpoint hedder noget andet
            const res = await fetch(`${apiUrl}/api/events/${eid}/functions`, {
                headers: getAuthHeaders(),
            });
            if (!res.ok) {
                setFunctionOptions([]);
                return;
            }

            const data = await res.json();

            const options: FunctionOption[] = (data as any[]).map((x) => ({
                value: String(x.functionId ?? x.FunctionId),
                label: String(x.functionName ?? x.FunctionName ?? x.name ?? x.Name),
            }));

            setFunctionOptions(options);
        } finally {
            setFunctionsLoading(false);
        }
    }

    async function loadSections(eid: number) {
        setLoading(true);
        setPageError(null);

        try {
            const res = await fetch(`${apiUrl}/api/admin/events/${eid}/files/sections`, {
                headers: getAuthHeaders(),
            });
            if (!res.ok) throw new Error("Kunne ikke hente sektioner.");

            const data = await res.json();

            const mapped: EventFileSectionDto[] = (data as any[]).map((s) => ({
                eventFileSectionID: s.eventFileSectionID ?? s.EventFileSectionID,
                eventID: s.eventID ?? s.EventID,
                name: s.name ?? s.Name,
                description: s.description ?? s.Description,
                sortOrder: s.sortOrder ?? s.SortOrder ?? 0,
                isActive: s.isActive ?? s.IsActive ?? true,
                isPublic: s.isPublic ?? s.IsPublic ?? false,
                functionIds: s.functionIds ?? s.FunctionIds ?? [],
                functionNames: s.functionNames ?? s.FunctionNames ?? [],
                files: ((s.files ?? s.Files ?? []) as any[]).map((f) => ({
                    fileID: f.fileID ?? f.FileID,
                    eventID: f.eventID ?? f.EventID,
                    eventFileSectionID: f.eventFileSectionID ?? f.EventFileSectionID,
                    name: f.name ?? f.Name,
                    originalFileName: f.originalFileName ?? f.OriginalFileName,
                    contentType: f.contentType ?? f.ContentType,
                    sizeBytes: f.sizeBytes ?? f.SizeBytes,
                    sortOrder: f.sortOrder ?? f.SortOrder,
                    createdAt: f.createdAt ?? f.CreatedAt,
                    downloadUrl: f.downloadUrl ?? f.DownloadUrl,
                })),
            }));

            setSections(mapped);
        } catch (err: any) {
            setPageError(err?.message || "Der opstod en fejl.");
            setSections([]);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        loadEvents();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (eventId == null) {
            setSections([]);
            setUploadSectionId(null);
            setFunctionOptions([]);
            return;
        }

        loadSections(eventId);
        loadFunctions(eventId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventId]);

    const sortedSections = useMemo(() => {
        return [...sections].sort(
            (a, b) => a.sortOrder - b.sortOrder || a.eventFileSectionID - b.eventFileSectionID
        );
    }, [sections]);

    const sectionSelectOptions = useMemo(() => {
        return sortedSections.map((s) => ({
            value: String(s.eventFileSectionID),
            label: s.name,
        }));
    }, [sortedSections]);

    function resetSectionForm() {
        setSectionEditItem(null);
        setSectionName("");
        setSectionDescription("");
        setSectionIsActive(true);
        setSectionIsPublic(false);
        setSectionFunctionIds([]);
    }

    function openCreateSection() {
        resetSectionForm();
        setSectionModalOpen(true);
    }

    function openEditSection(section: EventFileSectionDto) {
        setSectionEditItem(section);
        setSectionName(section.name);
        setSectionDescription(section.description ?? "");
        setSectionIsActive(section.isActive);
        setSectionIsPublic(section.isPublic);
        setSectionFunctionIds(section.functionIds.map(String));
        setSectionModalOpen(true);
    }

    async function saveSection() {
        if (eventId == null) return;

        const name = sectionName.trim();
        if (!name) return;

        setSectionSaving(true);
        try {
            const payload = {
                name,
                description: sectionDescription.trim() || null,
                isActive: sectionIsActive,
                isPublic: sectionIsPublic,
            };

            if (sectionEditItem) {
                const res = await fetch(
                    `${apiUrl}/api/admin/events/${eventId}/files/sections/${sectionEditItem.eventFileSectionID}`,
                    {
                        method: "PUT",
                        headers: getAuthHeaders({ "Content-Type": "application/json" }),
                        body: JSON.stringify(payload),
                    }
                );

                if (!res.ok) throw new Error("Kunne ikke opdatere sektion.");
            } else {
                const res = await fetch(`${apiUrl}/api/admin/events/${eventId}/files/sections`, {
                    method: "POST",
                    headers: getAuthHeaders({ "Content-Type": "application/json" }),
                    body: JSON.stringify(payload),
                });

                if (!res.ok) throw new Error("Kunne ikke oprette sektion.");
            }

            await loadSections(eventId);

            let currentSectionId = sectionEditItem?.eventFileSectionID ?? null;

            if (!currentSectionId) {
                const tempRes = await fetch(`${apiUrl}/api/admin/events/${eventId}/files/sections`, {
                    headers: getAuthHeaders(),
                });
                if (tempRes.ok) {
                    const tempData = await tempRes.json();
                    const tempMapped: EventFileSectionDto[] = (tempData as any[]).map((s) => ({
                        eventFileSectionID: s.eventFileSectionID ?? s.EventFileSectionID,
                        eventID: s.eventID ?? s.EventID,
                        name: s.name ?? s.Name,
                        description: s.description ?? s.Description,
                        sortOrder: s.sortOrder ?? s.SortOrder ?? 0,
                        isActive: s.isActive ?? s.IsActive ?? true,
                        isPublic: s.isPublic ?? s.IsPublic ?? false,
                        functionIds: s.functionIds ?? s.FunctionIds ?? [],
                        functionNames: s.functionNames ?? s.FunctionNames ?? [],
                        files: [],
                    }));

                    const matches = tempMapped
                        .filter((x) => x.name === name)
                        .sort((a, b) => b.sortOrder - a.sortOrder);

                    currentSectionId = matches[0]?.eventFileSectionID ?? null;
                }
            }

            if (currentSectionId) {
                const permissionRes = await fetch(
                    `${apiUrl}/api/admin/events/${eventId}/files/sections/${currentSectionId}/permissions`,
                    {
                        method: "PUT",
                        headers: getAuthHeaders({ "Content-Type": "application/json" }),
                        body: JSON.stringify({
                            functionIds: sectionFunctionIds.map(Number),
                        }),
                    }
                );

                if (!permissionRes.ok) {
                    const txt = await permissionRes.text();
                    throw new Error(txt || "Kunne ikke gemme funktioner på sektionen.");
                }
            }

            setSectionModalOpen(false);
            resetSectionForm();
            await loadSections(eventId);
        } catch (err: any) {
            setPageError(err?.message || "Kunne ikke gemme sektion.");
        } finally {
            setSectionSaving(false);
        }
    }

    async function deleteSection(section: EventFileSectionDto) {
        if (eventId == null) return;

        const ok = window.confirm(
            `Er du sikker på, at du vil slette sektionen "${section.name}"?`
        );
        if (!ok) return;

        try {
            const res = await fetch(
                `${apiUrl}/api/admin/events/${eventId}/files/sections/${section.eventFileSectionID}`,
                {
                    method: "DELETE",
                    headers: getAuthHeaders(),
                }
            );

            if (!res.ok) {
                const txt = await res.text();
                throw new Error(txt || "Kunne ikke slette sektion.");
            }

            await loadSections(eventId);
        } catch (err: any) {
            setPageError(err?.message || "Kunne ikke slette sektion.");
        }
    }

    async function reorderSections(newList: EventFileSectionDto[]) {
        if (eventId == null) return;

        const payload = newList.map((x, idx) => ({
            eventFileSectionID: x.eventFileSectionID,
            sortOrder: (idx + 1) * 10,
        }));

        const res = await fetch(`${apiUrl}/api/admin/events/${eventId}/files/sections/reorder`, {
            method: "PUT",
            headers: getAuthHeaders({ "Content-Type": "application/json" }),
            body: JSON.stringify(payload),
        });

        if (!res.ok) throw new Error("Kunne ikke ændre sektionsrækkefølge.");

        setSections(
            newList.map((x, idx) => ({
                ...x,
                sortOrder: (idx + 1) * 10,
            }))
        );
    }

    async function moveSectionUp(index: number) {
        if (index <= 0) return;
        const list = [...sortedSections];
        [list[index - 1], list[index]] = [list[index], list[index - 1]];
        await reorderSections(list);
    }

    async function moveSectionDown(index: number) {
        const list = [...sortedSections];
        if (index >= list.length - 1) return;
        [list[index], list[index + 1]] = [list[index + 1], list[index]];
        await reorderSections(list);
    }

    async function doUpload() {
        if (!uploadFile || eventId == null || !uploadSectionId) return;

        setUploading(true);
        try {
            const form = new FormData();
            form.append("file", uploadFile);
            if (uploadName.trim()) form.append("name", uploadName.trim());
            form.append("eventFileSectionID", uploadSectionId);

            const res = await fetch(`${apiUrl}/api/admin/events/${eventId}/files`, {
                method: "POST",
                body: form,
                headers: getAuthHeaders(),
            });

            if (!res.ok) {
                const txt = await res.text();
                throw new Error(txt || "Upload failed");
            }

            setUploadFile(null);
            setUploadName("");
            await loadSections(eventId);
        } catch (err: any) {
            setPageError(err?.message || "Kunne ikke uploade fil.");
        } finally {
            setUploading(false);
        }
    }

    function openEditFile(file: EventFileDto) {
        setFileEditItem(file);
        setFileEditName(file.name);
        setFileEditSectionId(file.eventFileSectionID ? String(file.eventFileSectionID) : null);
        setFileModalOpen(true);
    }

    async function saveFileEdit() {
        if (!fileEditItem || eventId == null) return;

        const name = fileEditName.trim();
        if (!name) return;

        setFileSaving(true);
        try {
            const res = await fetch(
                `${apiUrl}/api/admin/events/${eventId}/files/${fileEditItem.fileID}`,
                {
                    method: "PUT",
                    headers: getAuthHeaders({ "Content-Type": "application/json" }),
                    body: JSON.stringify({
                        name,
                        eventFileSectionID: fileEditSectionId ? Number(fileEditSectionId) : null,
                    }),
                }
            );

            if (!res.ok) {
                const txt = await res.text();
                throw new Error(txt || "Update failed");
            }

            setFileModalOpen(false);
            setFileEditItem(null);
            await loadSections(eventId);
        } catch (err: any) {
            setPageError(err?.message || "Kunne ikke gemme fil.");
        } finally {
            setFileSaving(false);
        }
    }

    async function deleteFile(file: EventFileDto) {
        if (eventId == null) return;

        const ok = window.confirm(`Er du sikker på, at du vil slette filen "${file.name}"?`);
        if (!ok) return;

        try {
            const res = await fetch(`${apiUrl}/api/admin/events/${eventId}/files/${file.fileID}`, {
                method: "DELETE",
                headers: getAuthHeaders(),
            });

            if (!res.ok) {
                const txt = await res.text();
                throw new Error(txt || "Delete failed");
            }

            await loadSections(eventId);
        } catch (err: any) {
            setPageError(err?.message || "Kunne ikke slette fil.");
        }
    }

    async function reorderFiles(section: EventFileSectionDto, newFiles: EventFileDto[]) {
        if (eventId == null) return;

        const payload = newFiles.map((x, idx) => ({
            fileID: x.fileID,
            sortOrder: (idx + 1) * 10,
        }));

        const res = await fetch(`${apiUrl}/api/admin/events/${eventId}/files/reorder`, {
            method: "PUT",
            headers: getAuthHeaders({ "Content-Type": "application/json" }),
            body: JSON.stringify(payload),
        });

        if (!res.ok) throw new Error("Kunne ikke ændre filrækkefølge.");

        setSections((prev) =>
            prev.map((s) =>
                s.eventFileSectionID !== section.eventFileSectionID
                    ? s
                    : {
                        ...s,
                        files: newFiles.map((f, idx) => ({
                            ...f,
                            sortOrder: (idx + 1) * 10,
                        })),
                    }
            )
        );
    }

    async function moveFileUp(section: EventFileSectionDto, index: number) {
        if (index <= 0) return;
        const files = [...section.files].sort(
            (a, b) => a.sortOrder - b.sortOrder || a.fileID - b.fileID
        );
        [files[index - 1], files[index]] = [files[index], files[index - 1]];
        await reorderFiles(section, files);
    }

    async function moveFileDown(section: EventFileSectionDto, index: number) {
        const files = [...section.files].sort(
            (a, b) => a.sortOrder - b.sortOrder || a.fileID - b.fileID
        );
        if (index >= files.length - 1) return;
        [files[index], files[index + 1]] = [files[index + 1], files[index]];
        await reorderFiles(section, files);
    }

    return (
        <div>
            <HeroSectionV2
                titleEn="Documentsystem"
                titleDa="Dokumentsystem"
                descriptionEn="Add and manage event files"
                descriptionDa="Tilføj og administrér eventfiler"
                imageSrc={Billede1}
            />

            <Container size="lg" py="xl">
                <Stack gap="lg">
                    <Paper withBorder p="lg" radius="md" shadow="xs">
                        <Text fw={600} mb="sm">
                            Vælg event
                        </Text>

                        <Select
                            placeholder={eventsLoading ? "Loader events..." : "Vælg event"}
                            data={eventOptions}
                            value={eventId == null ? null : String(eventId)}
                            onChange={(v) => setEventId(v ? Number(v) : null)}
                            searchable
                            nothingFoundMessage="Ingen events"
                            disabled={eventsLoading}
                        />

                        <Text size="xs" c="dimmed" mt="xs">
                            Vælg et event før du kan oprette sektioner og uploade dokumenter.
                        </Text>
                    </Paper>

                    {pageError && (
                        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
                            {pageError}
                        </Alert>
                    )}

                    <Paper withBorder p="lg" radius="md" shadow="xs">
                        <Group justify="space-between" align="center">
                            <div>
                                <Text fw={600}>Sektioner</Text>
                                <Text size="sm" c="dimmed" mt={4}>
                                    Opret opdelinger som fx Transport, Træning eller Generel info.
                                </Text>
                            </div>

                            <Group>
                                <Button
                                    variant="light"
                                    onClick={() => eventId != null && loadSections(eventId)}
                                    disabled={loading || eventId == null}
                                >
                                    Genindlæs
                                </Button>

                                <Button
                                    leftSection={<IconFolderPlus size={16} />}
                                    onClick={openCreateSection}
                                    disabled={eventId == null}
                                >
                                    Opret sektion
                                </Button>
                            </Group>
                        </Group>

                        {eventId == null ? (
                            <Text mt="md" c="dimmed">
                                Vælg et event for at arbejde med sektioner.
                            </Text>
                        ) : loading ? (
                            <Group mt="md">
                                <Loader size="sm" />
                                <Text>Loader…</Text>
                            </Group>
                        ) : sortedSections.length === 0 ? (
                            <Text mt="md" c="dimmed">
                                Ingen sektioner endnu.
                            </Text>
                        ) : (
                            <Stack mt="md" gap="md">
                                {sortedSections.map((section, sectionIndex) => {
                                    const sortedFiles = [...section.files].sort(
                                        (a, b) => a.sortOrder - b.sortOrder || a.fileID - b.fileID
                                    );

                                    return (
                                        <Paper key={section.eventFileSectionID} withBorder p="md" radius="md">
                                            <Stack gap="sm">
                                                <Group justify="space-between" align="flex-start">
                                                    <div>
                                                        <Group gap="xs">
                                                            <Text fw={700}>{section.name}</Text>
                                                            {!section.isActive && (
                                                                <Badge color="gray" variant="light">
                                                                    Inaktiv
                                                                </Badge>
                                                            )}
                                                            {section.isPublic ? (
                                                                <Badge color="green" variant="light">
                                                                    Alle
                                                                </Badge>
                                                            ) : (
                                                                <Badge color="blue" variant="light">
                                                                    Begrænset
                                                                </Badge>
                                                            )}
                                                        </Group>

                                                        {section.description && (
                                                            <Text size="sm" c="dimmed" mt={4}>
                                                                {section.description}
                                                            </Text>
                                                        )}

                                                        <Group gap="xs" mt="xs">
                                                            <Badge variant="light">
                                                                Sort: {section.sortOrder}
                                                            </Badge>

                                                            {!section.isPublic &&
                                                                section.functionNames.map((name) => (
                                                                    <Badge key={name} variant="outline">
                                                                        {name}
                                                                    </Badge>
                                                                ))}
                                                        </Group>
                                                    </div>

                                                    <Group gap={6}>
                                                        <ActionIcon
                                                            variant="light"
                                                            onClick={() => moveSectionUp(sectionIndex)}
                                                            disabled={sectionIndex === 0}
                                                        >
                                                            <IconArrowUp size={16} />
                                                        </ActionIcon>

                                                        <ActionIcon
                                                            variant="light"
                                                            onClick={() => moveSectionDown(sectionIndex)}
                                                            disabled={sectionIndex === sortedSections.length - 1}
                                                        >
                                                            <IconArrowDown size={16} />
                                                        </ActionIcon>

                                                        <ActionIcon
                                                            variant="light"
                                                            onClick={() => openEditSection(section)}
                                                        >
                                                            <IconPencil size={16} />
                                                        </ActionIcon>

                                                        <ActionIcon
                                                            variant="light"
                                                            color="red"
                                                            onClick={() => deleteSection(section)}
                                                        >
                                                            <IconTrash size={16} />
                                                        </ActionIcon>
                                                    </Group>
                                                </Group>

                                                <Divider />

                                                {sortedFiles.length === 0 ? (
                                                    <Text size="sm" c="dimmed">
                                                        Ingen filer i denne sektion endnu.
                                                    </Text>
                                                ) : (
                                                    <Table striped highlightOnHover withColumnBorders>
                                                        <Table.Thead>
                                                            <Table.Tr>
                                                                <Table.Th>Sort</Table.Th>
                                                                <Table.Th>Navn</Table.Th>
                                                                <Table.Th>Fil</Table.Th>
                                                                <Table.Th>Størrelse</Table.Th>
                                                                <Table.Th>Handling</Table.Th>
                                                            </Table.Tr>
                                                        </Table.Thead>
                                                        <Table.Tbody>
                                                            {sortedFiles.map((f, idx) => (
                                                                <Table.Tr key={f.fileID}>
                                                                    <Table.Td>
                                                                        <Group gap={6}>
                                                                            <ActionIcon
                                                                                variant="light"
                                                                                onClick={() => moveFileUp(section, idx)}
                                                                                disabled={idx === 0}
                                                                            >
                                                                                <IconArrowUp size={16} />
                                                                            </ActionIcon>
                                                                            <ActionIcon
                                                                                variant="light"
                                                                                onClick={() => moveFileDown(section, idx)}
                                                                                disabled={idx === sortedFiles.length - 1}
                                                                            >
                                                                                <IconArrowDown size={16} />
                                                                            </ActionIcon>
                                                                            <Badge variant="light">
                                                                                {f.sortOrder}
                                                                            </Badge>
                                                                        </Group>
                                                                    </Table.Td>
                                                                    <Table.Td>{f.name}</Table.Td>
                                                                    <Table.Td>
                                                                        <a
                                                                            href={`${apiUrl}${f.downloadUrl}`}
                                                                            target="_blank"
                                                                            rel="noreferrer"
                                                                        >
                                                                            {f.originalFileName}
                                                                        </a>
                                                                    </Table.Td>
                                                                    <Table.Td>{formatBytes(f.sizeBytes)}</Table.Td>
                                                                    <Table.Td>
                                                                        <Group gap={6}>
                                                                            <ActionIcon
                                                                                variant="light"
                                                                                onClick={() => openEditFile(f)}
                                                                            >
                                                                                <IconPencil size={16} />
                                                                            </ActionIcon>
                                                                            <ActionIcon
                                                                                variant="light"
                                                                                color="red"
                                                                                onClick={() => deleteFile(f)}
                                                                            >
                                                                                <IconTrash size={16} />
                                                                            </ActionIcon>
                                                                        </Group>
                                                                    </Table.Td>
                                                                </Table.Tr>
                                                            ))}
                                                        </Table.Tbody>
                                                    </Table>
                                                )}
                                            </Stack>
                                        </Paper>
                                    );
                                })}
                            </Stack>
                        )}
                    </Paper>

                    <Paper withBorder p="lg" radius="md" shadow="xs">
                        <Stack gap="md">
                            <div>
                                <Text fw={600}>Upload dokument</Text>
                                <Text size="sm" c="dimmed" mt={4}>
                                    Vælg sektion, fil og eventuelt et visningsnavn.
                                </Text>
                            </div>

                            <Grid align="end" gutter="md">
                                <Grid.Col span={{ base: 12, md: 4 }}>
                                    <Select
                                        label="Sektion"
                                        placeholder="Vælg sektion"
                                        data={sectionSelectOptions}
                                        value={uploadSectionId}
                                        onChange={setUploadSectionId}
                                        disabled={eventId == null || sortedSections.length === 0}
                                        nothingFoundMessage="Ingen sektioner"
                                    />
                                </Grid.Col>

                                <Grid.Col span={{ base: 12, md: 4 }}>
                                    <FileInput
                                        label="Fil"
                                        leftSection={<IconUpload size={16} />}
                                        placeholder="Vælg fil..."
                                        value={uploadFile}
                                        onChange={setUploadFile}
                                        disabled={eventId == null || !uploadSectionId}
                                        clearable
                                    />
                                </Grid.Col>

                                <Grid.Col span={{ base: 12, md: 3 }}>
                                    <TextInput
                                        label="Visningsnavn"
                                        placeholder="Valgfrit navn"
                                        value={uploadName}
                                        onChange={(e) => setUploadName(e.currentTarget.value)}
                                        disabled={eventId == null || !uploadSectionId}
                                    />
                                </Grid.Col>

                                <Grid.Col span={{ base: 12, md: 1 }}>
                                    <Button
                                        fullWidth
                                        loading={uploading}
                                        onClick={doUpload}
                                        disabled={!uploadFile || eventId == null || !uploadSectionId}
                                    >
                                        Upload
                                    </Button>
                                </Grid.Col>
                            </Grid>

                            <Text size="xs" c="dimmed">
                                Tilladt: pdf/doc/docx/xls/xlsx/png/jpg — max 50MB
                            </Text>
                        </Stack>
                    </Paper>

                    <Modal
                        opened={sectionModalOpen}
                        onClose={() => setSectionModalOpen(false)}
                        title={sectionEditItem ? "Redigér sektion" : "Opret sektion"}
                        centered
                        size="lg"
                    >
                        <Stack>
                            <TextInput
                                label="Navn"
                                value={sectionName}
                                onChange={(e) => setSectionName(e.currentTarget.value)}
                            />

                            <TextInput
                                label="Beskrivelse"
                                value={sectionDescription}
                                onChange={(e) => setSectionDescription(e.currentTarget.value)}
                                placeholder="Valgfri beskrivelse"
                            />

                            <Switch
                                label="Sektionen er aktiv"
                                checked={sectionIsActive}
                                onChange={(e) => setSectionIsActive(e.currentTarget.checked)}
                            />

                            <Switch
                                label="Alle må se sektionen"
                                checked={sectionIsPublic}
                                onChange={(e) => setSectionIsPublic(e.currentTarget.checked)}
                            />

                            <MultiSelect
                                label="Functions"
                                placeholder={
                                    functionsLoading
                                        ? "Loader funktioner..."
                                        : "Vælg funktioner"
                                }
                                data={functionOptions}
                                value={sectionFunctionIds}
                                onChange={setSectionFunctionIds}
                                searchable
                                disabled={sectionIsPublic}
                                nothingFoundMessage="Ingen funktioner"
                                description={
                                    sectionIsPublic
                                        ? "Denne sektion er offentlig for alle på eventet."
                                        : "Kun disse funktioner må se sektionen."
                                }
                            />

                            <Group justify="flex-end">
                                <Button variant="default" onClick={() => setSectionModalOpen(false)}>
                                    Annuller
                                </Button>
                                <Button loading={sectionSaving} onClick={saveSection}>
                                    Gem
                                </Button>
                            </Group>
                        </Stack>
                    </Modal>

                    <Modal
                        opened={fileModalOpen}
                        onClose={() => setFileModalOpen(false)}
                        title="Redigér fil"
                        centered
                    >
                        <Stack>
                            <TextInput
                                value={fileEditName}
                                onChange={(e) => setFileEditName(e.currentTarget.value)}
                                label="Navn"
                            />

                            <Select
                                label="Sektion"
                                placeholder="Vælg sektion"
                                data={sectionSelectOptions}
                                value={fileEditSectionId}
                                onChange={setFileEditSectionId}
                                nothingFoundMessage="Ingen sektioner"
                            />

                            <Group justify="flex-end">
                                <Button variant="default" onClick={() => setFileModalOpen(false)}>
                                    Annuller
                                </Button>
                                <Button loading={fileSaving} onClick={saveFileEdit}>
                                    Gem
                                </Button>
                            </Group>
                        </Stack>
                    </Modal>
                </Stack>
            </Container>
        </div>
    );
}